/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { logger, sanitizeError } from "../utils/logger.js";
/**
 * @fileoverview Users page: list, search, sort, and paginate users with Directory-style load more.
 * @module pages/Users
 */

import styles from "../styles/Users.module.css";

import { ReactComponent as TitleIcon } from "../assets/icons/Users/TitleIcon.svg";
import { ReactComponent as AddIcon } from "../assets/icons/UserViews/AddIcon.svg";
import { AppContext } from "../context/AppContext";
import {
  initializeUsersPage,
  fetchMoreUsersPage,
  sortUsers,
  paginateUsers,
  searchUsers,
  getUser,
  getUserRoleData,
} from "../logic/user";
import {
  handleBrowseUsersPageChange,
  handleSearchUsersPageChange,
} from "../logic/users/usersPagination.js";
import { canAccessUsers } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import UsersTable from "../components/UsersTable";
import ResultsDropdown from "../components/ResultsDropdown";
import SearchBar from "../components/SearchBar/index.jsx";
import PageSelector from "../components/PageSelector/index.jsx";

import { Backdrop, CircularProgress } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import { use, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";

import UserCreationModal from "../modals/UserCreationModal";
import UserDisplayModal from "../modals/UserDisplayModal";
import UserEditModal from "../modals/UserEditModal";
import UserDetailsView from "../components/UserDetailsView";
import UserCreationView from "../components/UserCreationView";
import UserEditView from "../components/UserEditView";

import { useTranslation } from "react-i18next";

/**
 * Users page: list, search, sort, and paginate users with Directory-style "load more".
 * Shows user details/edit/creation in modals (desktop) or full views (tablet/mobile).
 * Data is loaded via GET /user/page-data/{userId} with cursor-based load more.
 *
 * @returns {JSX.Element} The Users page component.
 */
const Users = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allowed } = useRequireAccess(canAccessUsers);
  const location = useLocation();
  const { changePermissions } = use(AppContext);
  const [usersData, setUsersData] = useState([]);
  const [sortedUsersList, setSortedUsersList] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "nombre",
    direction: "ascending",
  });
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [displayModalOpen, setDisplayModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [resultsLimit, setResultsLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [user, setUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showUserCreation, setShowUserCreation] = useState(false);
  const [showUserEdit, setShowUserEdit] = useState(false);
  const [companies, setCompanies] = useState({});
  const [lastKey, setLastKey] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchCompanies, setSearchCompanies] = useState({});
  const [browseUsersData, setBrowseUsersData] = useState([]);
  const [browseCompanies, setBrowseCompanies] = useState({});
  const [browseLastKey, setBrowseLastKey] = useState(null);
  const [browseCurrentPage, setBrowseCurrentPage] = useState(1);
  const [browseLimitReached, setBrowseLimitReached] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!!token && token.trim() !== "undefined") {
      getData();
    } else {
      logger.info("Error trying to get the token to retrieve the users data.");
    }
  }, []);

  // Handle window resize to switch between modal and view
  useEffect(() => {
    const handleResize = () => {
      // Handle user creation view/modal switching
      if (
        showUserCreation &&
        !isTablet() &&
        !isMediumMobile() &&
        !isSmallMobile()
      ) {
        setShowUserCreation(false);
        setOpen(true);
      } else if (open && (isTablet() || isMediumMobile() || isSmallMobile())) {
        setOpen(false);
        setShowUserCreation(true);
      } else {
        // No user creation view/modal switch needed for current width.
      }

      // Handle user details view/modal switching
      if (
        showUserDetails &&
        !isTablet() &&
        !isMediumMobile() &&
        !isSmallMobile()
      ) {
        setShowUserDetails(false);
        setDisplayModalOpen(true);
      } else if (
        displayModalOpen &&
        (isTablet() || isMediumMobile() || isSmallMobile())
      ) {
        setDisplayModalOpen(false);
        setShowUserDetails(true);
      } else {
        // No user details view/modal switch needed for current width.
      }

      // Handle user edit view/modal switching
      if (
        showUserEdit &&
        !isTablet() &&
        !isMediumMobile() &&
        !isSmallMobile()
      ) {
        setShowUserEdit(false);
        setEditModalOpen(true);
      } else if (
        editModalOpen &&
        (isTablet() || isMediumMobile() || isSmallMobile())
      ) {
        setEditModalOpen(false);
        setShowUserEdit(true);
      } else {
        // No user edit view/modal switch needed for current width.
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [
    showUserCreation,
    open,
    showUserDetails,
    displayModalOpen,
    showUserEdit,
    editModalOpen,
  ]);

  // Open current user's profile when navigating from side menu "Profile" button.
  // Wait for the Users page to finish loading so the list is visible behind the modal.
  useEffect(() => {
    const profileUser = location.state?.profileUser;
    if (!profileUser || isLoading) {
      return;
    }
    setSelectedUser(profileUser);
    const isDesktop = typeof window !== "undefined" && window.innerWidth > 1023;
    if (isDesktop) {
      setDisplayModalOpen(true);
    } else {
      setShowUserDetails(true);
    }
    navigate(location.pathname, {
      replace: true,
      state: { profileView: true },
    });
  }, [location.state?.profileUser, location.pathname, navigate, isLoading]);

  /**
   * Loads the first page of users and companies from GET /user/page-data/{userId}, then fetches
   * current user and permissions. Resets lastKey and limitReached.
   * @returns {Promise<void>}
   */
  const getData = async () => {
    try {
      setIsLoading(true);
      const userId = localStorage.getItem("userId");
      const data = await initializeUsersPage(userId, navigate);
      const users = data.users ?? [];
      const companiesMap = data.companies ?? {};
      setUsersData(users);
      setCompanies(companiesMap);
      setLastKey(data.lastEvaluatedKey ?? null);
      setBrowseUsersData(users);
      setBrowseCompanies(companiesMap);
      setBrowseLastKey(data.lastEvaluatedKey ?? null);
      setBrowseCurrentPage(1);
      setBrowseLimitReached(false);
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchCompanies({});
      setCurrentPage(1);
      setLimitReached(false);

      const [userData, permisos] = await Promise.all([
        getUser(userId, navigate),
        getUserRoleData(userId, navigate),
      ]);
      if (userData) {
        setUser(userData);
      }
      if (permisos != null) {
        changePermissions(permisos);
      }
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const sortedList = sortUsers(usersData, sortConfig);
    setSortedUsersList(sortedList);
  }, [usersData, sortConfig.direction]);

  /**
   * Toggles sort direction for the users table (ascending ↔ descending by name).
   */
  const handleSort = () => {
    const newDirection =
      sortConfig.direction === "ascending" ? "descending" : "ascending";
    setSortConfig({ key: "nombre", direction: newDirection });
  };

  /**
   * Renders the sort direction arrow icon (rotated when ascending).
   * @returns {JSX.Element} Arrow icon for the name column header.
   */
  const renderArrow = () => {
    return (
      <ArrowDownwardIcon
        className={`${styles.arrowIcon} ${
          sortConfig.direction === "ascending" ? styles.rotated : ""
        }`}
      />
    );
  };

  /**
   * Saves the current browse list, pagination cursor, and page so they can be restored after search.
   */
  const saveBrowseSnapshot = () => {
    setBrowseUsersData(usersData);
    setBrowseCompanies(companies);
    setBrowseLastKey(lastKey);
    setBrowseCurrentPage(currentPage);
    setBrowseLimitReached(limitReached);
  };

  /**
   * Restores the browse list and pagination state from the snapshot taken before search.
   */
  const restoreBrowseSnapshot = () => {
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchCompanies({});
    setUsersData(browseUsersData);
    setCompanies(browseCompanies);
    setLastKey(browseLastKey);
    setCurrentPage(browseCurrentPage);
    setLimitReached(browseLimitReached);
  };

  /**
   * Updates the search query from the search input (does not trigger search).
   * Restores browse mode when the input is cleared while search results are shown.
   * @param {React.ChangeEvent<HTMLInputElement>} event - Change event from the search input.
   */
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    if (value.trim() === "" && isSearchMode) {
      restoreBrowseSnapshot();
    }
  };

  /**
   * Submits the search query to GET /user/search/{userId}, or restores browse mode when empty.
   * @returns {Promise<void>}
   */
  const handleSearchSubmit = async () => {
    const query = searchQuery.trim();

    if (query === "") {
      if (isSearchMode) {
        restoreBrowseSnapshot();
      }
      return;
    }

    if (!isSearchMode) {
      saveBrowseSnapshot();
    }

    setCurrentPage(1);
    setIsSearching(true);
    try {
      const userId = localStorage.getItem("userId");
      const results = await searchUsers(userId, query, navigate);
      setSearchResults(results.users ?? []);
      setSearchCompanies(results.companies ?? {});
      setIsSearchMode(true);
      setLimitReached(false);
    } catch (error) {
      logger.error("Error searching users:", { err: sanitizeError(error) });
      setSearchResults([]);
      setSearchCompanies({});
      setIsSearchMode(true);
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Opens the selected user's details: full view on tablet/mobile, modal on desktop.
   * @param {Object} user - The user object to display.
   */
  const handleDisplayClick = (user) => {
    setSelectedUser(user);
    if (isTablet() || isMediumMobile() || isSmallMobile()) {
      setShowUserDetails(true);
    } else {
      setDisplayModalOpen(true);
    }
  };

  /**
   * Opens the selected user's edit UI: full view on tablet/mobile, modal on desktop.
   * @param {Object} user - The user object to edit.
   */
  const handleEditClick = (user) => {
    setSelectedUser(user);
    if (isTablet() || isMediumMobile() || isSmallMobile()) {
      setShowUserEdit(true);
    } else {
      setEditModalOpen(true);
    }
  };

  /**
   * Handles row/card click on tablet: shows user details view.
   * @param {Object} user - The user object to display.
   */
  const handleTabletCardClick = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  /**
   * Handles row/card click on mobile: shows user details view.
   * @param {Object} user - The user object to display.
   */
  const handleMobileCardClick = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  /**
   * Closes the user details view/modal and clears the selected user.
   */
  const handleBackToList = () => {
    setShowUserDetails(false);
    setDisplayModalOpen(false);
    setSelectedUser(null);
    if (location.state?.profileView) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  };

  /**
   * Closes the user creation view/modal.
   */
  const handleBackFromCreation = () => {
    setShowUserCreation(false);
  };

  /**
   * Closes the user edit view/modal.
   */
  const handleBackFromEdit = () => {
    setShowUserEdit(false);
  };

  /**
   * After editing a user: updates selection, refetches list, and closes details/edit.
   * @param {Object} updatedUser - The updated user object from the edit form.
   * @returns {Promise<void>}
   */
  const handleUpdateUser = async (updatedUser) => {
    setSelectedUser(updatedUser);
    await getData();
    setShowUserDetails(false);
    setDisplayModalOpen(false);
    setShowUserEdit(false);
  };

  /**
   * Updates how many users are shown per page and resets to page 1.
   * @param {{ value: number }} selectedOption - Option from ResultsDropdown (e.g. { value: 10 }).
   */
  const handleResultsLimitChange = (selectedOption) => {
    setResultsLimit(selectedOption.value);
    setCurrentPage(1);
    setLimitReached(false);
  };

  /**
   * Appends a newly created user to the list and closes the creation modal/view.
   * @param {Object} newUser - The user object returned from the creation flow.
   */
  const handleAddUser = (newUser) => {
    setUsersData((prevUsers) => {
      if (!Array.isArray(prevUsers)) {
        return [newUser];
      }
      return [...prevUsers, newUser];
    });
    setShowUserCreation(false);
    setOpen(false);
  };

  /**
   * Handles prev/next pagination. On "next" when on the last page, fetches more users via lastKey
   * (Directory-style load more); disables next when no more data (limitReached).
   * @param {"prev"|"next"} direction - Which page to go to.
   * @returns {Promise<void>}
   */
  const handlePageChange = async (direction) => {
    const totalPages = Math.ceil(filteredUsersList.length / resultsLimit) || 1;

    if (isSearchMode) {
      handleSearchUsersPageChange({
        direction,
        currentPage,
        totalPages,
        setCurrentPage,
      });
      return;
    }

    await handleBrowseUsersPageChange({
      direction,
      currentPage,
      totalPages,
      lastKey,
      userId: localStorage.getItem("userId"),
      navigate,
      fetchMoreUsersPage,
      setCurrentPage,
      setUsersData,
      setCompanies,
      setLastKey,
      setLimitReached,
      setIsLoading,
      onError: (error) => {
        logger.error("Error fetching more users:", {
          err: sanitizeError(error),
        });
      },
    });
  };

  /**
   * @returns {boolean} True if viewport is tablet width (768–1023px).
   */
  const isTablet = () => {
    return window.innerWidth >= 768 && window.innerWidth <= 1023;
  };

  /**
   * @returns {boolean} True if viewport is medium mobile width (481–767px).
   */
  const isMediumMobile = () => {
    return window.innerWidth >= 481 && window.innerWidth <= 767;
  };

  /**
   * @returns {boolean} True if viewport is small mobile width (≤480px).
   */
  const isSmallMobile = () => {
    return window.innerWidth <= 480;
  };

  /**
   * Opens user creation: full view on tablet/mobile, modal on desktop.
   */
  const handleCreateUserClick = () => {
    if (isTablet() || isMediumMobile() || isSmallMobile()) {
      setShowUserCreation(true);
    } else {
      setOpen(true);
    }
  };

  const baseList = isSearchMode
    ? sortUsers(searchResults, sortConfig)
    : sortedUsersList;

  // Non-collaborator admins still filter by RFC (backend returns all users for admins).
  const filteredUsersList = baseList.filter((userData) => {
    if (String(user?.tipoDeUsuario) === "2") {
      return true;
    }
    if (user && !user.esColaborador) {
      return userData.RFC === user.RFC;
    }
    return true;
  });

  const totalPages = Math.ceil(filteredUsersList.length / resultsLimit) || 1;
  const isNextDisabled = isSearchMode
    ? currentPage >= totalPages
    : limitReached;

  const displayCompanies = isSearchMode ? searchCompanies : companies;

  const paginatedUsersList = paginateUsers(
    filteredUsersList,
    currentPage,
    resultsLimit,
  );

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      {showUserDetails && selectedUser ? (
        <UserDetailsView
          user={selectedUser}
          onBack={handleBackToList}
          onUpdateUser={handleUpdateUser}
          currentUser={user}
          onEditClick={handleEditClick}
        />
      ) : showUserCreation ? (
        <UserCreationView
          onBack={handleBackFromCreation}
          onAddUser={handleAddUser}
          user={user}
        />
      ) : showUserEdit && selectedUser ? (
        <UserEditView
          user={selectedUser}
          onBack={handleBackFromEdit}
          onUpdateUser={handleUpdateUser}
          currentUser={user}
        />
      ) : (
        <>
          <div className={styles.header}>
            <div className={styles.languages}>
              <NotificationBell navigate={navigate} />
              <LanguageSelector />
            </div>

            {/* Page title and search bar */}
            <div className={styles.headerInfoContainer}>
              <div className={styles.headerInfo}>
                <TitleIcon />
                <h1 className={styles.title}>{t("pages.users.header")}</h1>
              </div>
              <div className={styles.searchSection}>
                <div className={styles.searchBarWrapper}>
                  <SearchBar
                    placeholder={t("pages.users.searchText")}
                    value={searchQuery}
                    onChange={handleSearch}
                    onSearch={handleSearchSubmit}
                    width="100%"
                  />
                </div>
                {user &&
                  user?.tipoDeUsuario !== "3" &&
                  user?.tipoDeUsuario !== "4" &&
                  user?.tipoDeUsuario !== "12" && (
                    <div
                      className={styles.newUserButton}
                      onClick={handleCreateUserClick}
                    >
                      <AddIcon />
                      {isTablet()
                        ? t("pages.users.tabletNewUser")
                        : isSmallMobile() || isMediumMobile()
                          ? ""
                          : t("pages.users.newUser")}
                    </div>
                  )}

                {/* Modal Dialog */}
                <UserCreationModal
                  open={open}
                  onClose={() => setOpen(false)}
                  onAddUser={handleAddUser}
                  user={user}
                />
              </div>
            </div>
          </div>

          <div className={styles.mainContent}>
            <UsersTable
              users={paginatedUsersList}
              sortConfig={sortConfig}
              onSort={handleSort}
              onDisplayClick={handleDisplayClick}
              onEditClick={handleEditClick}
              onTabletCardClick={handleTabletCardClick}
              onMobileCardClick={handleMobileCardClick}
              renderArrow={renderArrow}
              currentUser={user}
              companies={displayCompanies}
            />

            <div className={styles.footerContainer}>
              <div className={styles.footer}>
                {/* Number of results dropdown */}
                <ResultsDropdown
                  onResultsLimitChange={handleResultsLimitChange}
                />

                {/* Page selector (Directory-style: next disabled only when limitReached) */}
                <PageSelector
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  disabled={{ next: isNextDisabled }}
                />
              </div>
            </div>
          </div>

          {/* User Display Modal */}
          {selectedUser && (
            <UserDisplayModal
              open={displayModalOpen}
              onClose={() => setDisplayModalOpen(false)}
              user={selectedUser}
              onEditClick={handleEditClick}
              onUpdateUser={handleUpdateUser}
              currentUser={user}
            />
          )}

          {/* User Edit Modal */}
          {selectedUser && (
            <UserEditModal
              open={editModalOpen}
              onClose={() => setEditModalOpen(false)}
              user={selectedUser}
              onUpdateUser={handleUpdateUser}
            />
          )}
        </>
      )}

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading || isSearching}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default Users;
